import React, { useEffect, useState, useCallback } from 'react';
import { products as productsApi } from '../utils/api';
import { fmtDate, fmtUSD, fmtRMB } from '../utils/helpers';
import { useToast, ToastContainer } from '../hooks/useToast';
import { useT } from '../i18n/I18nContext';

export default function Products() {
  const t = useT();
  const [list, setList]         = useState([]);
  const [selected, setSelected] = useState(null);
  const [history, setHistory]   = useState([]);
  const [loadingH, setLoadingH] = useState(false);
  const [search, setSearch]     = useState('');
  const { toasts, toast }       = useToast();

  const load = useCallback(() => productsApi.list().then(setList), []);
  useEffect(() => { load(); }, [load]);

  const filtered = list.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.category||'').toLowerCase().includes(search.toLowerCase())
  );

  async function openProduct(p) {
    setSelected(p);
    setLoadingH(true);
    try {
      const h = await productsApi.history(p.id);
      setHistory(h);
    } catch { toast(t('products.error_loading'), 'error'); }
    finally { setLoadingH(false); }
  }

  if (selected) {
    return (
      <div className="page">
        <ToastContainer toasts={toasts} />
        <div className="page-header">
          <div>
            <button className="btn btn-ghost btn-sm" onClick={() => setSelected(null)}>{t('btn.back')}</button>
            <div className="page-title" style={{ marginTop: 8 }}>📦 {selected.name}</div>
            {selected.category && <div className="page-subtitle">{selected.category}</div>}
          </div>
        </div>

        <div className="card">
          <div className="card-header"><div className="card-title">{t('products.price_history')}</div></div>
          <div style={{ padding: 0, overflow: 'hidden' }}>
            {loadingH ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-400)' }}>{t('common.loading')}</div>
            ) : history.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-400)' }}>{t('products.no_history')}</div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('products.col.quotation')}</th>
                    <th>{t('col.supplier')}</th>
                    <th>{t('products.col.date')}</th>
                    <th>{t('products.col.qty')}</th>
                    <th>{t('products.col.init_price')}</th>
                    <th>{t('products.col.confirmed_price')}</th>
                    <th>{t('col.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => (
                    <tr key={h.item_id}>
                      <td>
                        <a href={`/quotations/${h.quotation_id}`} style={{ color: 'var(--pantone)', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                          {h.reference}
                        </a>
                        {h.collection && <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{h.collection}</div>}
                      </td>
                      <td>{h.supplier_name}<div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{h.supplier_country}</div></td>
                      <td>{fmtDate(h.quotation_date)}</td>
                      <td>{h.quantity || '—'}</td>
                      <td style={{ fontFamily: 'var(--font-mono)' }}>
                        {h.initial_price ? (h.currency === 'RMB' ? fmtRMB(h.initial_price) : fmtUSD(h.initial_price)) : '—'}
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono)', color: 'var(--pantone)', fontWeight: 600 }}>
                        {h.confirmed_price ? (h.currency === 'RMB' ? fmtRMB(h.confirmed_price) : fmtUSD(h.confirmed_price)) : '—'}
                      </td>
                      <td><span className={`badge badge-${(h.status||'').toLowerCase().replace(/ /g,'-')}`}>{h.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  }

  const subtitle = list.length === 1
    ? `1 ${t('products.title').toLowerCase().slice(0,-1)} cadastrado`
    : `${list.length} ${t('products.title').toLowerCase()} cadastrados`;

  return (
    <div className="page">
      <ToastContainer toasts={toasts} />
      <div className="page-header">
        <div>
          <div className="page-title">📦 {t('products.title')}</div>
          <div className="page-subtitle">{subtitle}</div>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('products.search_placeholder')}
          style={{ maxWidth: 320 }}
        />
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="table">
          <thead>
            <tr>
              <th>{t('col.name')}</th>
              <th>{t('col.category')}</th>
              <th>{t('col.obs') || 'Observações'}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 32 }}>
                {search ? t('products.not_found') : t('products.empty')}
              </td></tr>
            )}
            {filtered.map(p => (
              <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => openProduct(p)}>
                <td style={{ fontWeight: 600, color: 'var(--pantone)' }}>{p.name}</td>
                <td>{p.category || '—'}</td>
                <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>{p.notes || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
